import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { useInvalidateWalletBalances } from "@/hooks/useEnvironmentWallets";
import { toast } from "sonner";

const POLL_MS = 1500;
const TIMEOUT_MS = 20000;

export interface DepositSyncState {
  /** True while a credited deposit is being reflected into wallet balances. */
  isSyncing: boolean;
  /** Currency being credited (uppercase), or null. */
  syncingCurrency: string | null;
  /** Amount being credited, or null. */
  pendingAmount: number | null;
  /** Timestamp of the last confirmed balance update. */
  lastCreditedAt: number | null;
  /** True when the balance did not visibly change before the timeout. */
  timedOut: boolean;
  /** Manually re-trigger a balance refresh (used by the "tap to refresh" affordance). */
  refreshBalances: () => void;
  /** True when the given currency (or any, when omitted) is currently syncing. */
  isSyncingCurrency: (currency?: string) => boolean;
}

const DepositBalanceSyncContext = createContext<DepositSyncState>({
  isSyncing: false,
  syncingCurrency: null,
  pendingAmount: null,
  lastCreditedAt: null,
  timedOut: false,
  refreshBalances: () => {},
  isSyncingCurrency: () => false,
});

export function useDepositBalanceSync() {
  return useContext(DepositBalanceSyncContext);
}

export function DepositBalanceSyncProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, session } = useAuth();
  const { isSandbox } = usePlatformSettings();
  const qc = useQueryClient();
  const invalidateBalances = useInvalidateWalletBalances();

  const [syncingCurrency, setSyncingCurrency] = useState<string | null>(null);
  const [pendingAmount, setPendingAmount] = useState<number | null>(null);
  const [lastCreditedAt, setLastCreditedAt] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const invalidateRef = useRef(invalidateBalances);
  invalidateRef.current = invalidateBalances;

  const walletTable = isSandbox ? "sandbox_wallets" : "wallets";

  const clearTimers = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pollRef.current = null;
    timeoutRef.current = null;
  }, []);

  const refreshBalances = useCallback(() => {
    invalidateRef.current();
    qc.invalidateQueries({ queryKey: ["environment_wallets"] });
    setTimedOut(false);
  }, [qc]);

  const finish = useCallback(
    (currency: string, amount: number | null, success: boolean) => {
      clearTimers();
      setSyncingCurrency(null);
      setPendingAmount(null);
      if (success) {
        setLastCreditedAt(Date.now());
        setTimedOut(false);
        toast.success(
          amount && amount > 0
            ? `Deposit credited: +${amount} ${currency}`
            : `Deposit credited in ${currency}`
        );
      } else {
        setTimedOut(true);
      }
      invalidateRef.current();
    },
    [clearTimers]
  );

  const startSync = useCallback(
    (currency: string, amount: number | null, uid: string) => {
      clearTimers();
      setSyncingCurrency(currency);
      setPendingAmount(amount);
      setTimedOut(false);

      let baseline: number | null = null;
      const started = Date.now();

      const readBalance = async (): Promise<number | null> => {
        const { data, error } = await supabase
          .from(walletTable as any)
          .select("balance")
          .eq("user_id", uid)
          .eq("currency", currency);
        if (error) return null;
        return ((data as any[]) || []).reduce((sum, w) => sum + Number(w.balance || 0), 0);
      };

      // Capture the pre-credit balance, then poll until it moves.
      void (async () => {
        baseline = await readBalance();
        invalidateRef.current();

        pollRef.current = setInterval(async () => {
          const current = await readBalance();
          invalidateRef.current();
          if (current !== null && (baseline === null || current > baseline)) {
            finish(currency, amount, true);
          } else if (Date.now() - started > TIMEOUT_MS) {
            finish(currency, amount, false);
          }
        }, POLL_MS);

        timeoutRef.current = setTimeout(() => finish(currency, amount, false), TIMEOUT_MS);
      })();
    },
    [clearTimers, finish, walletTable]
  );

  useEffect(() => {
    if (!user?.id || authLoading || !session) return;
    const uid = user.id;

    const channel = supabase
      .channel(`deposit-balance-sync-${uid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "crypto_transactions", filter: `user_id=eq.${uid}` },
        (payload) => {
          const row: any = payload.new;
          const prev: any = payload.old;
          if (!row) return;
          if ((row.type || "").toLowerCase() !== "deposit") return;

          const creditedNow = !!row.credit_applied_at || row.status === "credited";
          const creditedBefore = !!prev?.credit_applied_at || prev?.status === "credited";
          if (!creditedNow || creditedBefore) return;

          const key = `${row.id}:${row.credit_applied_at || row.status}`;
          if (seenRef.current.has(key)) return;
          seenRef.current.add(key);

          const currency = String(row.currency || "").toUpperCase();
          const amount = Number(row.credited_amount ?? row.amount) || null;
          if (!currency) return;
          startSync(currency, amount, uid);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      clearTimers();
    };
  }, [user?.id, authLoading, session, startSync, clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const value = useMemo<DepositSyncState>(() => {
    const isSyncing = syncingCurrency !== null;
    return {
      isSyncing,
      syncingCurrency,
      pendingAmount,
      lastCreditedAt,
      timedOut,
      refreshBalances,
      isSyncingCurrency: (currency?: string) => {
        if (!isSyncing) return false;
        if (!currency) return true;
        return currency.toUpperCase() === syncingCurrency;
      },
    };
  }, [syncingCurrency, pendingAmount, lastCreditedAt, timedOut, refreshBalances]);

  return (
    <DepositBalanceSyncContext.Provider value={value}>{children}</DepositBalanceSyncContext.Provider>
  );
}
